import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import MainLogo from '../Titles/MainLogo';
import Subtitle from '../Texts/Subtitle';
import OutlineButton from '../Buttons/OutlineButton';
import MusicStyleSelector from './MusicStyleSelector';

type CompleteMusicStylesModalProps = {
  userId: string | null;
  onComplete: (styles: string[]) => Promise<void>;
};

const CompleteMusicStylesModal: React.FC<CompleteMusicStylesModalProps> = ({
  userId,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!userId) {
      setSelected([]);
      setError('');
    }
  }, [userId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (selected.length === 0) {
      setError(t('auth.musicStylesRequired'));
      return;
    }

    setSaving(true);

    try {
      await onComplete(selected);
      setSelected([]);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : t('auth.musicStylesSaveFailed'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(userId)}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="music-styles-title"
      slotProps={{
        paper: {
          sx: {
            border: '5px solid',
            borderColor: 'primary.light',
            borderRadius: '20px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.19)',
            overflow: 'hidden',
          },
        },
        backdrop: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, sm: 3.5 }, textAlign: 'center', bgcolor: 'common.white' }}>
        <MainLogo />
        <Subtitle id="music-styles-title">{t('auth.musicStylesTitle')}</Subtitle>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 1 }}>
          {t('auth.musicStylesDescription')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('auth.musicStylesHint')}
        </Typography>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box component="form" onSubmit={handleSubmit}>
          <MusicStyleSelector selected={selected} onChange={setSelected} disabled={saving} />
          <OutlineButton type="submit" disabled={saving || selected.length === 0}>
            {saving ? <CircularProgress size={24} color="inherit" /> : t('auth.musicStylesContinue')}
          </OutlineButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteMusicStylesModal;
